<script setup>
import { computed, onMounted, ref } from 'vue';
import { commissionSkyPayoutsApi } from 'dashboard/api/commissionsSky';

const loading = ref(true);
const payouts = ref([]);
const summary = ref({});
const filterStatus = ref('');
const filterUser = ref('');
const expandedId = ref(null);
const expandedItems = ref([]);
const busyId = ref(null);

const formatCents = c =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    (parseInt(c, 10) || 0) / 100
  );

const formatDate = d => {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

const METHOD_LABEL = { pix: 'PIX', credit_card: 'Cartão', boleto: 'Boleto' };
const methodLabel = m => METHOD_LABEL[m] || m || '—';

const statusClass = s =>
  ({
    paid: 'bg-emerald-500/15 text-emerald-600',
    open: 'bg-blue-500/15 text-blue-600',
    reopened: 'bg-amber-500/15 text-amber-600',
  })[s] || 'bg-n-alpha-2 text-n-slate-11';
const statusLabel = s =>
  ({ paid: 'Pago', open: 'Em aberto', reopened: 'Reaberto' })[s] || s;

// Opções de consultor derivadas dos próprios repasses carregados.
const consultantOptions = computed(() => {
  const map = new Map();
  payouts.value.forEach(p => {
    if (p.user_id) map.set(p.user_id, p.user_name || `#${p.user_id}`);
  });
  return Array.from(map, ([id, name]) => ({ id, name }));
});

async function fetchPayouts() {
  loading.value = true;
  try {
    const params = {};
    if (filterStatus.value) params.status = filterStatus.value;
    if (filterUser.value) params.user_id = filterUser.value;
    const res = await commissionSkyPayoutsApi.list(params);
    payouts.value = res.data.payload || [];
    summary.value = res.data.summary || {};
  } catch {
    payouts.value = [];
  } finally {
    loading.value = false;
  }
}

async function toggleExpand(payout) {
  if (expandedId.value === payout.id) {
    expandedId.value = null;
    return;
  }
  expandedId.value = payout.id;
  expandedItems.value = [];
  try {
    const res = await commissionSkyPayoutsApi.show(payout.id);
    expandedItems.value = res.data.payload.items || [];
  } catch {
    expandedItems.value = [];
  }
}

async function markPaid(payout) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Marcar o repasse de ${payout.user_name} como PAGO?`))
    return;
  busyId.value = payout.id;
  try {
    await commissionSkyPayoutsApi.markPaid(payout.id);
    await fetchPayouts();
  } finally {
    busyId.value = null;
  }
}

async function reopen(payout) {
  busyId.value = payout.id;
  try {
    await commissionSkyPayoutsApi.reopen(payout.id);
    await fetchPayouts();
  } finally {
    busyId.value = null;
  }
}

onMounted(fetchPayouts);
</script>

<template>
  <div class="flex flex-col h-full overflow-auto bg-n-background">
    <div class="mx-auto w-full max-w-6xl p-4 sm:p-6 space-y-5">
      <!-- Header -->
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-xl font-bold text-n-slate-12">
            <span class="i-lucide-hand-coins text-n-brand" />
            Comissões SKY — Repasses
          </h1>
          <p class="mt-0.5 text-sm text-n-slate-10">
            Repasse semanal por consultor, com base na entrada de caixa
          </p>
        </div>
      </div>

      <!-- Summary -->
      <div class="grid grid-cols-2 gap-3 sm:max-w-md">
        <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-3">
          <p class="text-[10px] uppercase tracking-wider text-n-slate-10">
            Em aberto (a pagar)
          </p>
          <p class="mt-1 text-lg font-bold text-blue-600">
            {{ formatCents(summary.open_cents) }}
          </p>
        </div>
        <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-3">
          <p class="text-[10px] uppercase tracking-wider text-n-slate-10">
            Pago no mês
          </p>
          <p class="mt-1 text-lg font-bold text-emerald-600">
            {{ formatCents(summary.paid_month_cents) }}
          </p>
        </div>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap items-center gap-2">
        <select
          v-model="filterUser"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          @change="fetchPayouts"
        >
          <option value="">Todos os consultores</option>
          <option v-for="c in consultantOptions" :key="c.id" :value="c.id">
            {{ c.name }}
          </option>
        </select>
        <select
          v-model="filterStatus"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          @change="fetchPayouts"
        >
          <option value="">Todos os status</option>
          <option value="open">Em aberto</option>
          <option value="paid">Pago</option>
          <option value="reopened">Reaberto</option>
        </select>
      </div>

      <div
        v-if="loading"
        class="flex items-center justify-center py-20 text-n-slate-10"
      >
        <span class="i-lucide-loader-2 animate-spin text-2xl" />
      </div>

      <!-- Table -->
      <div
        v-else-if="payouts.length"
        class="overflow-x-auto rounded-xl border border-n-weak/40"
      >
        <table class="w-full min-w-[640px] text-sm">
          <thead>
            <tr
              class="border-b border-n-weak/40 bg-n-alpha-1 text-left text-n-slate-10"
            >
              <th class="px-3 py-2 font-medium">Consultor</th>
              <th class="px-3 py-2 font-medium">Sexta</th>
              <th class="px-3 py-2 font-medium text-center">Parcelas</th>
              <th class="px-3 py-2 font-medium text-right">Total</th>
              <th class="px-3 py-2 font-medium text-center">Status</th>
              <th class="px-3 py-2 font-medium text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="p in payouts" :key="p.id">
              <tr class="border-b border-n-weak/20 hover:bg-n-alpha-1">
                <td class="px-3 py-2 font-medium text-n-slate-12">
                  {{ p.user_name || `#${p.user_id}` }}
                </td>
                <td class="px-3 py-2 text-n-slate-11">
                  {{ formatDate(p.payout_date) }}
                </td>
                <td class="px-3 py-2 text-center text-n-slate-11">
                  {{ p.count }}
                </td>
                <td class="px-3 py-2 text-right font-semibold text-n-slate-12">
                  {{ formatCents(p.total_amount_cents) }}
                </td>
                <td class="px-3 py-2 text-center">
                  <span
                    class="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    :class="statusClass(p.status)"
                    >{{ statusLabel(p.status) }}</span
                  >
                </td>
                <td class="px-3 py-2">
                  <div class="flex items-center justify-end gap-1.5">
                    <button
                      class="rounded-md border border-n-weak px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                      @click="toggleExpand(p)"
                    >
                      {{ expandedId === p.id ? 'Fechar' : 'Ver' }}
                    </button>
                    <button
                      v-if="p.status !== 'paid'"
                      class="rounded-md bg-emerald-500 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
                      :disabled="busyId === p.id"
                      @click="markPaid(p)"
                    >
                      Marcar pago
                    </button>
                    <button
                      v-else
                      class="rounded-md border border-amber-500/50 px-2 py-1 text-xs text-amber-600 hover:bg-amber-500/10 disabled:opacity-50"
                      :disabled="busyId === p.id"
                      @click="reopen(p)"
                    >
                      Reabrir
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="expandedId === p.id" class="bg-n-alpha-1/50">
                <td colspan="6" class="px-4 py-3">
                  <div v-if="expandedItems.length" class="space-y-1.5">
                    <div
                      v-for="i in expandedItems"
                      :key="i.id"
                      class="flex items-center justify-between gap-2 text-xs"
                    >
                      <span class="flex items-center gap-2">
                        <span
                          class="rounded bg-n-alpha-2 px-1.5 py-0.5 font-semibold text-n-slate-11"
                          >{{ methodLabel(i.method) }}</span
                        >
                        <span class="text-n-slate-10"
                          >bruto {{ formatCents(i.gross_amount_cents) }} · sexta
                          {{ formatDate(i.release_date) }}</span
                        >
                      </span>
                      <span class="font-semibold text-emerald-600">{{
                        formatCents(i.commission_amount_cents)
                      }}</span>
                    </div>
                  </div>
                  <p v-else class="text-xs text-n-slate-10">
                    Sem parcelas neste repasse.
                  </p>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-else class="py-20 text-center text-n-slate-10">
        Nenhum repasse ainda. Eles aparecem quando a comissão de uma venda entra
        no caixa.
      </div>
    </div>
  </div>
</template>
